const HUBSPOT_CONTACT_CONFIG_ID = 'hubspot-contact-config';
const HUBSPOT_API_BASE = 'https://api.hsforms.com/submissions/v3/integration/submit';
const HUBSPOT_COOKIE_NAME = 'hubspotutk';
const HUBSPOT_PROCESSING_CONSENT_TEXT = 'I agree to allow InkPoster to process my personal data.';
const HUBSPOT_CONTACT_FORM_SELECTOR = '.contact-form';
const HUBSPOT_CONTACT_BOUND_ATTRIBUTE = 'hubspotContactBound';
const HUBSPOT_EXCLUDED_CONTACT_FIELDS = new Set(['agreement', 'type', 'body']);
const HUBSPOT_ALLOWED_CONTACT_TYPES = new Set(['business', 'art', 'publicRelations']);
let sectionListenerAttached = false;
let currentConfig = null;

function logDebug(config, message, details) {
  if (!config?.debugMode) {
    return;
  }

  if (details !== undefined) {
    console.debug('[HubSpot Contact]', message, details);

    return;
  }

  console.debug('[HubSpot Contact]', message);
}

function parseJson(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function parseSubscriptionTypeId(subscriptionTypeId) {
  if (subscriptionTypeId === null || typeof subscriptionTypeId === 'undefined') {
    return null;
  }

  const normalizedValue = String(subscriptionTypeId).trim();

  if (!normalizedValue) {
    return null;
  }

  const numericValue = Number(normalizedValue);

  if (!Number.isInteger(numericValue) || numericValue <= 0) {
    return null;
  }

  return numericValue;
}

function getCookieValue(name) {
  if (!name || !document.cookie) {
    return null;
  }

  const cookies = document.cookie.split(';').map((cookie) => cookie.trim());
  const targetCookie = cookies.find((cookie) => cookie.startsWith(`${name}=`));

  if (!targetCookie) {
    return null;
  }

  const cookieValue = targetCookie.slice(name.length + 1);

  return cookieValue || null;
}

function parseConfig() {
  const configScript = document.getElementById(HUBSPOT_CONTACT_CONFIG_ID);

  if (!configScript) {
    return null;
  }

  return parseJson(configScript.textContent);
}

function normalizeIdentifier(value) {
  if (typeof value !== 'string' && typeof value !== 'number') {
    return null;
  }

  const normalizedValue = String(value).trim();

  if (!normalizedValue) {
    return null;
  }

  return normalizedValue;
}

function normalizeFieldName(fieldName) {
  const match = /^contact\[(.+)\]$/.exec(fieldName);

  if (!match) {
    return null;
  }

  return match[1];
}

function detectFormType(formElement) {
  const formType = formElement.dataset.hubspotContactType || null;

  if (!HUBSPOT_ALLOWED_CONTACT_TYPES.has(formType)) {
    return null;
  }

  return formType;
}

function buildContactFields(formElement) {
  const formData = new FormData(formElement);
  const fields = [];

  for (const [fieldName, rawFieldValue] of formData.entries()) {
    if (typeof rawFieldValue !== 'string') {
      continue;
    }

    const normalizedName = normalizeFieldName(fieldName);

    if (!normalizedName || HUBSPOT_EXCLUDED_CONTACT_FIELDS.has(normalizedName)) {
      continue;
    }

    const normalizedValue = rawFieldValue.trim();

    if (!normalizedValue) {
      continue;
    }

    fields.push({
      name: normalizedName,
      value: normalizedValue,
    });
  }

  return fields;
}

function buildPayload({ fields, subscriptionTypeId, communicationValue, trackingEnabled }) {
  const hubspotUserToken = trackingEnabled ? getCookieValue(HUBSPOT_COOKIE_NAME) : null;

  const payload = {
    submittedAt: Date.now(),
    fields,
    context: {
      pageUri: globalThis.location.href,
      pageName: document.title,
    },
    legalConsentOptions: {
      consent: {
        consentToProcess: true,
        text: HUBSPOT_PROCESSING_CONSENT_TEXT,
        communications: [
          {
            value: communicationValue,
            subscriptionTypeId,
            text: 'Contact form',
          },
        ],
      },
    },
  };

  if (hubspotUserToken) {
    payload.context.hutk = hubspotUserToken;
  }

  return payload;
}

function sendHubspotContactSubmission(config, formElement) {
  const portalId = normalizeIdentifier(config?.portalId);

  if (!portalId) {
    logDebug(config, 'Submit skipped because portalId is missing or invalid.', {
      rawPortalId: config?.portalId,
      formId: formElement.id,
    });

    return;
  }

  const emailInput = formElement.querySelector('input[name="contact[email]"]');
  const trimmedEmail = emailInput?.value?.trim();

  if (!trimmedEmail) {
    logDebug(config, 'Submit skipped because email is empty.', {
      formId: formElement.id,
    });

    return;
  }

  const parsedSubscriptionTypeId = parseSubscriptionTypeId(config.subscriptionTypeId);

  if (!parsedSubscriptionTypeId) {
    logDebug(config, 'Submit skipped because subscriptionTypeId is missing or invalid.', {
      rawSubscriptionTypeId: config.subscriptionTypeId,
      formId: formElement.id,
    });

    return;
  }

  const formType = detectFormType(formElement);

  if (!formType) {
    logDebug(config, 'Submit skipped because form type cannot be detected.', {
      formId: formElement.id,
    });

    return;
  }

  const rawFormId = config?.formIds?.[formType];
  const formId = normalizeIdentifier(rawFormId);

  if (!formId) {
    logDebug(config, 'Submit skipped because HubSpot formId for detected type is missing.', {
      detectedFormType: formType,
      formId: formElement.id,
      rawFormId,
    });

    return;
  }

  const fields = buildContactFields(formElement);

  if (!fields.length) {
    logDebug(config, 'Submit skipped because no valid contact fields were collected.', {
      formId: formElement.id,
      formType,
    });

    return;
  }

  const consentCheckbox = formElement.querySelector('input[name="contact[agreement]"]');
  const communicationValue = consentCheckbox ? consentCheckbox.checked : true;
  const endpointUrl = `${HUBSPOT_API_BASE}/${portalId}/${formId}`;

  const requestPayload = buildPayload({
    fields,
    subscriptionTypeId: parsedSubscriptionTypeId,
    communicationValue,
    trackingEnabled: config.trackingEnabled ?? false,
  });

  logDebug(config, 'Submitting contact payload to HubSpot.', {
    endpointUrl,
    formType,
    fieldsCount: fields.length,
    communicationValue,
  });

  void fetch(endpointUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestPayload),
    keepalive: true,
  })
    .then(async (response) => {
      if (!response.ok) {
        let responseText = '';

        try {
          responseText = await response.text();
        } catch {
          responseText = '';
        }

        console.error('[HubSpot Contact] HubSpot submission failed.', {
          status: response.status,
          ok: response.ok,
          formType,
          formId: formElement.id,
          responseText,
        });

        return;
      }

      logDebug(config, 'HubSpot contact submission completed.', {
        status: response.status,
        ok: response.ok,
      });
    })
    .catch((error) => {
      console.error('[HubSpot Contact] HubSpot submission error.', {
        formType,
        formId: formElement.id,
        error: error instanceof Error ? error.message : String(error),
      });

      logDebug(config, 'HubSpot contact submission failed.', {
        error: error instanceof Error ? error.message : String(error),
      });
    });
}

function bindContactForms(config) {
  const contactForms = document.querySelectorAll(HUBSPOT_CONTACT_FORM_SELECTOR);

  logDebug(config, 'Binding contact forms.', {
    formsCount: contactForms.length,
  });

  contactForms.forEach((formElement) => {
    if (formElement.dataset[HUBSPOT_CONTACT_BOUND_ATTRIBUTE] === 'true') {
      return;
    }

    formElement.dataset[HUBSPOT_CONTACT_BOUND_ATTRIBUTE] = 'true';
    formElement.addEventListener('submit', () => {
      sendHubspotContactSubmission(config, formElement);
    });
  });
}

export function initHubspotContact() {
  const config = parseConfig();
  const portalId = normalizeIdentifier(config?.portalId);

  if (!portalId) {
    logDebug(config, 'Initialization skipped due to missing config.', {
      hasPortalId: Boolean(portalId),
    });

    return;
  }

  currentConfig = config;

  bindContactForms(config);

  if (sectionListenerAttached) {
    logDebug(config, 'Section listener already attached.');

    return;
  }

  sectionListenerAttached = true;

  document.addEventListener('shopify:section:load', () => {
    const refreshedConfig = parseConfig();

    const portalIdFromDom = normalizeIdentifier(refreshedConfig?.portalId);

    const hasAnyConfiguredFormId = Boolean(
      normalizeIdentifier(refreshedConfig?.formIds?.business) ||
      normalizeIdentifier(refreshedConfig?.formIds?.art) ||
      normalizeIdentifier(refreshedConfig?.formIds?.publicRelations)
    );

    logDebug(refreshedConfig, 'shopify:section:load detected. Rebinding contact forms.', {
      hasPortalId: Boolean(portalIdFromDom),
      hasAnyConfiguredFormId,
    });

    if (!portalIdFromDom || !hasAnyConfiguredFormId) {
      return;
    }

    currentConfig = refreshedConfig;

    bindContactForms(refreshedConfig);
  });
}

initHubspotContact();
